import { Fragment, Node, Slice } from '@tiptap/pm/model';
import AWS from 'aws-sdk';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import Papa from 'papaparse';
import chatService from 'services/chat';
import { Divider, Stack, Text } from 'shell/components';
import { expireAuthSession } from 'utils/auth';
import { ChatRole, getChatExportMessageText, sortChatMessagesByCreatedOn } from 'utils/chat';
import {
    AI_BATCH_CANCEL_URL,
    AI_FEEDBACKS_URL,
    AI_GRADE_URL,
    AI_REGENERATE_URL,
    AI_RUBRICS_URL,
    CITATION_SELECT_OPTIONS,
    DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
    DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
    DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
    DEFAULT_FEEDBACK_TONE,
    DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH,
    DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL,
    DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE,
    DEFAULT_INLINE_COMMENTS_TONE,
    DurationFormat,
    Placeholder,
} from 'utils/constants';
import { uploadFile } from 'utils/file';
import {
    getRubricOptionLevel,
    isUnscoredRubric,
    normalizeRubricText,
    usesCompleteIncompleteGrading,
} from 'utils/rubricScoring';

export const getUserPreferredFullName = (data) => {
    const { first_name = '', last_name = '', display_name = '', email = '' } = data || {};

    if (display_name) {
        return `${display_name || ''} ${last_name || ''}`.trim();
    }

    return `${first_name || ''} ${last_name || ''}`.trim() || email;
};

export const getS3FileURL = (data) => {
    try {
        if (!data) return;
        const { bucket, key, name } = data;
        if (!bucket || !key || !name) return '';

        const s3 = new AWS.S3();
        // If name is provided, join it to the key (strip any trailing slash)
        const objectKey = name ? `${key.replace(/\/$/, '')}/${name}` : key;

        const params = {
            Bucket: bucket,
            Key: objectKey,
            Expires: 7200,
        };
        const signedURL = s3.getSignedUrl('getObject', params);
        // getSignedUrl returns the URL string
        return signedURL;
    } catch (e) {
        return window.location.reload();
    }
};

export const bytesToMB = (bytes) => {
    const MB = 1000000; // 1 MB = 1,000,000 bytes
    const mbValue = bytes / MB;
    return mbValue % 1 === 0 ? mbValue : parseFloat(mbValue.toFixed(2));
};

// si - true to use metric (SI) units, aka powers of 1000, false to use binary (IEC), aka powers of 1024
// dp - number of decimal places to display
export const getPrettyFileSize = (bytes, si = false, dp = 1) => {
    const thresh = si ? 1000 : 1024;

    if (Math.abs(bytes) < thresh) {
        return bytes + ' B';
    }

    const units = si
        ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
        : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
    let u = -1;
    const r = 10 ** dp;

    do {
        bytes /= thresh;
        ++u;
    } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

    return bytes.toFixed(dp) + ' ' + units[u];
};

export const getPrettyFileType = (mimeType, t) => {
    if (mimeType.startsWith('video/')) return 'Video';
    switch (mimeType) {
        case 'application/pdf':
            return t('common:resource-type.pdf', 'PDF');
        case 'application/msword':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            return t('common:resource-type.word', 'Word');
        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            return t('common:resource-type.excel', 'Excel');
        case 'application/vnd.ms-powerpoint':
        case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
            return t('common:resource-type.ppt', 'PowerPoint');
        case 'application/zip':
            return t('common:resource-type.zip', 'ZIP');
        case 'image/gif':
        case 'image/jpeg':
        case 'image/png':
            return t('common:resource-type.image', 'Image');
        case 'text/csv':
            return t('common:resource-type.csv', 'CSV');
        case 'text/html':
            return t('common:resource-type.html', 'HTML');
        case 'text/plain':
            return t('common:resource-type.text', 'Text');
        default:
            return t('common:resource-type.file', 'File');
    }
};

export const downloadFileFromURL = (url, name) => {
    const anchor = document.createElement('a');
    anchor.style.display = 'none';
    anchor.href = url;
    anchor.download = name;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
};

export const getAbsoluteURL = (url) => {
    return ['http://', 'https://'].some((prefix) => url.startsWith(prefix)) ? url : `https://${url}`;
};

export const downloadCSV = (filename, csv) => {
    const unparsed = Papa.unparse(csv);
    const blob = new Blob([unparsed], { type: 'text/plain;charset=utf-8' });
    return downloadFileFromURL(URL.createObjectURL(blob), filename);
};

const formatChatCsvTimestamp = (value, t) => {
    if (!t) return moment(value || Date.now()).format('YYYY-MM-DD HH:mm:ss');

    return t('common:ui.format-date-time-seconds', { val: value });
};

const getChatSenderRole = (role, t) => {
    if (role === ChatRole.ASSISTANT) return t('common:ui.assistant', 'Assistant');
    if (role === ChatRole.USER) return t('common:ui.student', 'Student');
    return t('common:ui.system', 'System');
};

const getChatSenderName = (role, studentName, t) => {
    if (role === ChatRole.ASSISTANT) return t('common:ui.ai', 'AI');
    if (role === ChatRole.USER) return studentName;
    return t('common:ui.system', 'System');
};

const getChatCsvMessageText = (message, t) => {
    return stripMarkdown(
        getChatExportMessageText({
            message,
            t,
            timeLabel: t('common:ui.format-time-only', { val: message.created_on || new Date() }),
        })
    );
};

export const exportInstructorFormativeChatToCSV = async ({
    assignment,
    course,
    ownerAccountId,
    studentAccountUser,
    t,
}) => {
    const ChatCsvHeaders = [
        t('common:table.timestamp', 'Timestamp'),
        t('common:table.sender-name', 'Sender Name'),
        t('common:table.role', 'Role'),
        t('common:table.message-text', 'Message Text'),
    ];

    if (!assignment?.id || !course?.id || !ownerAccountId) return;

    const studentName = getUserPreferredFullName(studentAccountUser) || t('common:ui.student', 'Student');
    const [chat] = await chatService.listChats({
        assignment_id: assignment.id,
        course_id: course.id,
        owner_account_id: ownerAccountId,
    });

    const filenameBase = `${assignment?.title || 'assignment'}_${studentName}_ChatHistoryExport`
        .toLowerCase()
        .replaceAll(' ', '_');

    if (!chat?.id) {
        return downloadCSV(`${filenameBase}.csv`, [ChatCsvHeaders]);
    }

    const rows = sortChatMessagesByCreatedOn(chat?.messages || []).map((message) => {
        const senderName = getChatSenderName(message.role, studentName, t);

        return [
            formatChatCsvTimestamp(message.created_on, t),
            senderName,
            getChatSenderRole(message.role, t),
            getChatCsvMessageText(message, t),
        ];
    });

    return downloadCSV(`${filenameBase}.csv`, [ChatCsvHeaders, ...rows]);
};

export const isHTML = (string) =>
    typeof string === 'string' &&
    /^\s*</.test(string) &&
    (string.startsWith('<p') ||
        string.startsWith('<ul') ||
        string.startsWith('<ol') ||
        string.startsWith('<li') ||
        string.startsWith('<div') ||
        string.startsWith('<br') ||
        string.includes('<b') ||
        string.includes('<strong'));

export const stripHTML = (input) => {
    if (input == null) return '';
    const str = String(input);
    if (!isHTML(str)) return str;

    const div = document.createElement('div');
    div.innerHTML = str;

    function process(node) {
        // Text
        if (node.nodeType === 3) return node.nodeValue || '';

        // Non-element (comments, etc.)
        if (node.nodeType !== 1) return '';

        const tag = node.tagName.toLowerCase();

        // Recursively collect children text
        const childrenText = [...node.childNodes].map(process).join('');

        // Explicit handlers
        if (tag === 'br') return '\n';
        if (tag === 'hr') return '\n';

        if (tag === 'p') {
            // Emit a newline even for empty <p></p>
            const hasContent = (node.textContent || '').replace(/\u00A0/g, ' ').trim().length > 0;
            return (hasContent ? childrenText : '') + '\n';
        }

        if (tag === 'li') {
            // Parent will add bullets/numbers; just return content
            return childrenText.trim();
        }

        if (tag === 'ul') {
            const lines = [...node.children].map((li) => '- ' + process(li).trim());
            return lines.join('\n') + '\n';
        }

        if (tag === 'ol') {
            const lines = [...node.children].map((li, i) => `${i + 1}. ` + process(li).trim());
            return lines.join('\n') + '\n';
        }

        // Treat common block containers like paragraphs: content + newline
        if (/^(div|section|article|header|footer|blockquote|h[1-6])$/.test(tag)) {
            return childrenText + '\n';
        }

        // Inline-ish: just pass through
        return childrenText;
    }

    let out = [...div.childNodes].map(process).join('');

    // Normalize
    out = out
        .replace(/\r\n/g, '\n') // Windows -> Unix
        .replace(/\u00A0/g, ' ') // NBSP to space
        .replace(/[ \t]+\n/g, '\n') // trim trailing spaces before newline
        .replace(/[ \t]{2,}/g, ' ') // collapse runs of spaces
        .trim();

    return out;
};

export const stripMarkdown = (input) => {
    if (input == null) return '';

    return String(input)
        .replace(/```[\s\S]*?```/g, (block) => block.replace(/```/g, '').trim())
        .replace(/`([^`]+)`/g, '$1')
        .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .replace(/^>\s?/gm, '')
        .replace(/^#{1,6}\s*/gm, '')
        .replace(/^[-*+]\s+/gm, '')
        .replace(/^\d+\.\s+/gm, '')
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/\*([^*]+)\*/g, '$1')
        .replace(/__([^_]+)__/g, '$1')
        .replace(/_([^_]+)_/g, '$1')
        .replace(/~~([^~]+)~~/g, '$1')
        .trim();
};

// RTE handler for pasting
export const clipboardTextParser = (text, context) => {
    const blocks = text.replace().split(/(?:\r\n?|\n)/);
    const nodes = [];
    blocks.forEach((line) => {
        const nodeJSON = { type: 'paragraph' };
        if (line.length) {
            nodeJSON.content = [{ type: 'text', text: line }];
        }
        const node = Node.fromJSON(context.doc.type.schema, nodeJSON);
        nodes.push(node);
    });
    return Slice.maxOpen(Fragment.fromArray(nodes));
};

export const getTooltipComponent = (title, description) => {
    return (
        <Stack divider={<Divider />} spacing={1}>
            <Text color={'inherit'} variant={'p3'} weight={'semibold'}>
                {title}
            </Text>
            <Stack>
                <Text color={'inherit'} variant={'p3'}>
                    {description}
                </Text>
            </Stack>
        </Stack>
    );
};

export const getTooltipProps = (tooltip) => {
    const { component, maxWidth, placement, ...rest } = tooltip;
    return {
        title: component,
        maxWidth: maxWidth || '8.75rem',
        enterDelay: 300,
        placement: placement || 'bottom',
        ...rest,
    };
};

export const insertAtIndex = (arr, start, ...items) => {
    return [...arr.slice(0, start), ...items, ...arr.slice(start)];
};

export const getWordCount = (string) => {
    if (!string) return 0;
    const trimmed = string.trim();
    if (!trimmed.length) return 0;
    return trimmed.split(' ').length;
};

export const getHTMLWordCount = (html) => {
    function stripHtmlTagsWithNewlines(input) {
        const replaced = input.replace(/<\/?(p|div|br|h[1-6]|li|ul|ol|table|tr|td|th)[^>]*>/gi, '\n');
        const stripped = replaced.replace(/<\/?[^>]+(>|$)/g, '');
        return stripped.replace(/\n\s*\n/g, '\n').trim();
    }

    const clean = stripHtmlTagsWithNewlines(html);
    if (!clean.length) return 0;
    return clean.replaceAll('\n', ' ').split(/\s+/).length;
};

// given a value, return true if the value is a valid rate (for rubrics)
// a valid rate must be a non-negative number
export const isValidRate = (value) => {
    if (typeof value !== 'string') return false;

    // check if the string is a valid non-negative number
    // this regex matches integers and decimals without signs
    const nonNegativeNumberRegex = /^(?:\d+|\d*\.\d+)$/;

    return nonNegativeNumberRegex.test(value);
};

// given a value, return true if the value is a natural number
// natural numbers are non-negative integers
export const isNatural = (value) => {
    const parsed = parseInt(value);
    return !isNaN(parsed) && Number.isInteger(parsed) && parsed > -1;
};

export const getChatCompletion = async (url, body, signal) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error();
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body,
        signal,
    });

    if (response.status === 401) {
        expireAuthSession();
        throw new Error('Session expired');
    }

    return response;
};

export const extractFeedbackPlainAndHtml = (html = '') => {
    if (!html) return '';
    // Replace empty paras with a placeholder newline
    html = html
        .replace(/&nbsp;/gi, ' ')
        .replace(/\u00a0/g, ' ')

        // Turn structural breaks into newlines BEFORE stripping tags
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|section|article|li|ul|ol|h[1-6])\s*>/gi, '\n\n')

        // Optionally add a marker for list items (comment out if you don't want it)
        // .replace(/<li[^>]*>/gi, '- ')
        .replace(/<li[^>]*>/gi, '')

        // Strip the rest of the tags
        .replace(/<[^>]+>/g, '')

        // Clean up spacing
        .replace(/[ \t]+\n/g, '\n') // trim right side of lines
        .replace(/\n+/g, '\n') // collapse multiple newlines
        .replace(/\s+/g, ' ') // collapse spaces

        // Ensure a space after sentence punctuation when it ran together
        .replace(/([.!?])([A-Za-z])/g, '$1 $2')

        .trim();
    var txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
};

export const getAIPreview = async ({
    target,
    course_id,
    owner_account_id,
    assignment,
    submission,
    connections,
    language = 'English',
    signal,
}) => {
    const URL = {
        grading: AI_GRADE_URL,
        feedback: AI_FEEDBACKS_URL,
        both: AI_REGENERATE_URL,
    }[target];

    const payload = JSON.stringify({
        target,
        course_id,
        assignment: {
            ...assignment,
            description: stripHTML(assignment.description),
            citation_format: assignment.citation_format ?? CITATION_SELECT_OPTIONS[0].key,
            ai_feedback_settings: {
                ...assignment.ai_feedback_settings,
                custom_instructions: stripHTML(assignment.ai_feedback_settings.custom_instructions),
            },
            ai_feedback_context: stripHTML(assignment.ai_feedback_context),
            ai_grading_context: stripHTML(assignment.ai_grading_context),
        },
        submission,
        platform: process.env.REACT_APP_API_ENDPOINT.split('/').slice(-1),
        language,
        connections,
        ws_url: process.env.REACT_APP_WEBSOCKET_URL.replace('wss', 'https'),
    });
    const blob = new Blob([payload], { type: 'application/json' });

    const fileName = 'AI_PREVIEW.json';
    const JSONFile = new File([blob], fileName, { type: 'application/json' });

    const file = await new Promise(async (resolve, reject) => {
        await uploadFile(
            JSONFile,
            () => {},
            (data) => {
                resolve(data);
            },
            (error) => {
                console.error(error);
                reject(error);
            },
            owner_account_id
        );
    });

    return await getChatCompletion(URL, JSON.stringify(file), signal);
};

export const getRubric = async ({
    title,
    description,
    numCriteria,
    useCustomCriteria,
    criteria,
    maxScore,
    numOptions,
    language = 'English',
    type,
    rubricSource,
}) => {
    return await getChatCompletion(
        AI_RUBRICS_URL,
        type
            ? JSON.stringify({
                  type,
                  rubricSource,
                  language,
              })
            : JSON.stringify({
                  title,
                  description: stripHTML(description),
                  numCriteria: Number(numCriteria),
                  useCustomCriteria,
                  criteria,
                  maxScore: Number(maxScore),
                  numOptions: Number(numOptions),
                  language,
              })
    );
};

export const getSubmissionGrade = (assignment, submission) => {
    if (!submission.grading) return 0;
    const score = Object.entries(submission.grading.criteria).reduce((acc, [criterionId, optionId]) => {
        const { rate } = assignment.rubric.criteria
            .find(({ id }) => id === criterionId)
            .options.find(({ id }) => id === optionId);
        return acc + Number(rate);
    }, 0);
    return score;
};

export const isAssignmentValid = (assignment) => {
    const isTitleValid = () => {
        return Boolean(assignment.title.trim());
    };

    const isDescriptionValid = () => {
        return Boolean(assignment.description.trim());
    };

    const isRubricValid = () => {
        if (!Boolean(assignment.rubric?.title.trim())) return false;
        if (isUnscoredRubric(assignment)) return true;
        return assignment.rubric.criteria.every((criterion) => {
            return criterion.options.every((option) => {
                return isValidRate(option.rate);
            });
        });
    };

    return isTitleValid() && isDescriptionValid() && isRubricValid();
};

export const buildSubmissionsCSV = (course, assignment, submissions, accountUsers, t) => {
    const readOnly = t('common:ui.read-only', 'read only');
    const unscoredRubric = isUnscoredRubric(assignment);

    const gradedSubmissions = submissions.filter(({ grading }) => grading);
    const totalPoints = assignment.rubric.criteria.reduce((acc, cur) => {
        return acc + Math.max(...cur.options.map(({ rate }) => Number(rate)));
    }, 0);

    const formatFeedbackStr = (feedbackObj) => {
        if (!feedbackObj) return Placeholder.NO_DATA;
        let str = '';

        if (typeof feedbackObj === 'string') {
            str = stripHTML(feedbackObj);
        } else {
            // Check all common commentary fields
            const commentary = feedbackObj.commentary || feedbackObj.feedback || feedbackObj.text || '';
            str += stripHTML(commentary);

            const suggestions = feedbackObj.improvement_suggestions || [];

            if (feedbackObj.feedback_type === 'rubric') {
                const noFeedbackTranslationArgs = assignment?.api_assignment_id
                    ? [
                          'feedback-section.no-feedback-no-pushback',
                          'No feedback provided. This message will not be passed back.',
                      ]
                    : ['feedback-section.no-feedback', 'No feedback provided.'];
                const positive_feedback =
                    course?.language && course?.language === 'Spanish'
                        ? '\n\n- Lo que has hecho bien:\n\n'
                        : '\n\n- What you did well:\n\n';
                const negative_feedback =
                    course?.language && course?.language === 'Spanish'
                        ? '\n\n- Cómo mejorar:\n'
                        : '\n\n- How you can improve:\n';

                suggestions
                    .filter(({ active }) => active !== false)
                    .forEach(({ criterion_id, positive, negative, text }, index) => {
                        if (index || commentary) {
                            str += '\n\n';
                        }

                        // Handle System vs Rubric structure
                        if (feedbackObj.feedback_type === 'system' || (text && !positive && !negative)) {
                            str += stripHTML(text || '');
                        } else {
                            let criterion = assignment.rubric.criteria.find(({ id }) => id === criterion_id);
                            str += `${criterion?.title ?? ''}\n\n`;

                            if (positive) {
                                str += positive_feedback;
                                str +=
                                    positive.active === false
                                        ? t('feedback-section.deleted-feedback', 'This feedback was deleted.')
                                        : positive.text === ''
                                          ? t(...noFeedbackTranslationArgs)
                                          : stripHTML(positive.text);
                            }

                            if (negative) {
                                str += negative_feedback;
                                str +=
                                    negative.active === false
                                        ? t('feedback-section.deleted-feedback', 'This feedback was deleted.')
                                        : negative.text === ''
                                          ? t(...noFeedbackTranslationArgs)
                                          : stripHTML(negative.text);
                            }
                        }
                    });
            } else if (feedbackObj.feedback_type === 'system') {
                feedbackObj.improvement_suggestions
                    .filter(({ active }) => active)
                    .forEach(({ text }, index) => {
                        if (index || feedbackObj.commentary) {
                            str += '\n\n';
                        }
                        str += stripHTML(text);
                    });
            }
        }

        return str.trim() || Placeholder.NO_DATA;
    };

    let suggestedAverageTotalPoints = '';
    try {
        suggestedAverageTotalPoints = submissions.length
            ? submissions.reduce((acc, cur) => {
                  const totalPoints = assignment.rubric.criteria.reduce((acc, { id, options }) => {
                      const optionId = cur.ai?.grading?.[id]?.optionId;
                      if (!optionId) return acc;
                      const option = options.find((o) => o.id === optionId);
                      return acc + (option ? Number(option.rate) : 0);
                  }, 0);
                  return acc + totalPoints;
              }, 0) / submissions.length
            : Placeholder.NO_DATA;
    } catch (e) {
        suggestedAverageTotalPoints = Placeholder.NO_DATA;
    }

    let actualAverageTotalPoints = '';
    try {
        actualAverageTotalPoints = gradedSubmissions.length
            ? gradedSubmissions.reduce((acc, cur) => {
                  const totalPoints =
                      cur.grade_overrides?.total ??
                      assignment.rubric.criteria.reduce((acc, { id, options }) => {
                          const rate =
                              cur.grade_overrides?.[id] ??
                              options.find(({ id }) => id === cur.grading.criteria[id])?.rate;
                          return acc + Number(rate || 0);
                      }, 0);
                  return acc + Number(totalPoints);
              }, 0) / gradedSubmissions.length
            : Placeholder.NO_DATA;
    } catch (e) {
        actualAverageTotalPoints = Placeholder.NO_DATA;
    }

    const parseName = (str) => {
        const PLATFORM = process.env.REACT_APP_KIND;
        // removes appended dynamic "(user_id)"
        const namePart = str.replace(/\s*\([^)]*\)\s*$/, '');
        if (PLATFORM === 'D2L') {
            const [first, last] = namePart.split(' ').map((s) => s.trim());
            return { first: first || '', last: last || '' };
        }
        if (namePart.includes(',')) {
            const [last, first] = namePart.split(',').map((s) => s.trim());
            return { first, last };
        }
        return { first: namePart.trim(), last: '' };
    };

    if (unscoredRubric) {
        const formatLevel = (criterion, optionId) => {
            const level = getRubricOptionLevel(criterion, optionId);
            return level === null ? Placeholder.NO_DATA : t('rubric.level-value', 'Lv. {{level}}', { level });
        };

        const formatRating = (criterion, optionId) =>
            criterion.options.find(({ id }) => `${id}` === `${optionId}`)?.title || Placeholder.NO_DATA;

        const formatSemanticGrade = (submission) => {
            if (!submission.grading) return Placeholder.NO_DATA;
            if (!usesCompleteIncompleteGrading(assignment)) {
                return t('common:status.reviewed', 'Reviewed');
            }

            const completionStatus = `${submission.grading.status || 'complete'}`.toLowerCase();
            const labels = {
                complete: t('common:status.complete', 'Complete'),
                incomplete: t('common:status.incomplete', 'Incomplete'),
                excused: t('common:status.excused', 'Excused'),
            };
            return labels[completionStatus] || labels.complete;
        };

        return [
            [
                'File Name',
                ...(assignment.api_assignment_id ? ['First Name', 'Last Name', 'Section'] : []),
                ...assignment.rubric.criteria.reduce((acc, { title }, index) => {
                    acc.push(`Suggested Level Criterion ${index + 1}: ${title}`);
                    acc.push(`Suggested Rating Criterion ${index + 1}: ${title}`);
                    return acc;
                }, []),
                'Final Grade',
                ...assignment.rubric.criteria.reduce((acc, { title }, index) => {
                    acc.push(`Criterion ${index + 1} Level: ${title}`);
                    acc.push(`Criterion ${index + 1} Rating: ${title}`);
                    return acc;
                }, []),
                t('common:table.grading-time-mmss', 'Grading Time (mm:ss)'),
                t('common:table.graded-by', 'Graded By'),
                t('common:ui.suggested-feedback', 'Suggested Feedback'),
                t('common:ui.feedback', 'Feedback'),
            ],
            ...submissions.map((submission) => {
                const suggestedFeedback = formatFeedbackStr(submission.ai?.feedback);
                const finalFeedback = formatFeedbackStr(submission.grading?.feedback);

                return [
                    `${submission.files
                        .map(({ name, original_file }) => (original_file ? original_file.name : name))
                        .join(', ')}`,
                    ...(assignment.api_assignment_id
                        ? [
                              parseName(submission?.api?.user?.name ?? '').first,
                              parseName(submission?.api?.user?.name ?? '').last,
                              submission?.api?.user?.sections?.map(({ name }) => name)?.join(', ') ?? '--',
                          ]
                        : []),
                    ...assignment.rubric.criteria.reduce((acc, criterion) => {
                        const optionId = submission.ai?.grading?.[criterion.id]?.optionId;
                        acc.push(formatLevel(criterion, optionId));
                        acc.push(formatRating(criterion, optionId));
                        return acc;
                    }, []),
                    formatSemanticGrade(submission),
                    ...assignment.rubric.criteria.reduce((acc, criterion) => {
                        const optionId = submission.grading?.criteria?.[criterion.id];
                        acc.push(formatLevel(criterion, optionId));
                        acc.push(formatRating(criterion, optionId));
                        return acc;
                    }, []),
                    getSubmissionGradingTime(submission),
                    getSubmissionGradedBy(submission, accountUsers),
                    suggestedFeedback,
                    finalFeedback,
                ];
            }),
        ];
    }

    const csv = [
        [
            'File Name',
            ...(assignment.api_assignment_id ? ['First Name', 'Last Name', 'Section'] : []),
            'Final Suggested Grade',
            ...assignment.rubric.criteria.reduce((acc, { title }, index) => {
                acc.push(`Suggested Grade Criterion ${index + 1}: ${title}`);
                acc.push(`Suggested Rating Criterion ${index + 1}: ${title}`);
                return acc;
            }, []),
            'Final Grade',
            ...assignment.rubric.criteria.reduce((acc, { title }, index) => {
                acc.push(`Criterion ${index + 1}: ${title}`);
                acc.push(`Criterion ${index + 1} Rating: ${title}`);
                return acc;
            }, []),
            t('common:table.grading-time-mmss', 'Grading Time (mm:ss)'),
            t('common:table.graded-by', 'Graded By'),
            t('common:ui.suggested-feedback', 'Suggested Feedback'),
            t('common:ui.feedback', 'Feedback'),
        ],
        [
            'Points Possible',
            ...(assignment.api_assignment_id ? [`(${readOnly})`, `(${readOnly})`, `(${readOnly})`] : []),
            `${totalPoints}`,
            ...assignment.rubric.criteria.reduce((acc, { options }) => {
                acc.push(Math.max(...options.map(({ rate }) => rate)).toString());
                acc.push(`(${readOnly})`);
                return acc;
            }, []),
            `${totalPoints}`,
            ...assignment.rubric.criteria.reduce((acc, { options }) => {
                acc.push(Math.max(...options.map(({ rate }) => rate)).toString());
                acc.push(`(${readOnly})`);
                return acc;
            }, []),
            `(${readOnly})`,
            `(${readOnly})`,
            `(${readOnly})`,
            `(${readOnly})`,
        ],
        [
            'Class Average',
            ...(assignment.api_assignment_id ? [`(${readOnly})`, `(${readOnly})`, `(${readOnly})`] : []),
            `${
                typeof suggestedAverageTotalPoints === 'number'
                    ? Number(suggestedAverageTotalPoints.toFixed(3))
                    : suggestedAverageTotalPoints
            }`,
            ...assignment.rubric.criteria.reduce((acc, { id: criterionId, options }) => {
                try {
                    const criterionTotal = submissions.reduce((acc, cur) => {
                        const optionId = cur.ai?.grading?.[criterionId]?.optionId;
                        return acc + Number(options.find(({ id }) => id === optionId)?.rate ?? 0);
                    }, 0);
                    acc.push(
                        submissions.length
                            ? `${Number((criterionTotal / submissions.length).toFixed(3))}`
                            : Placeholder.NO_DATA
                    );
                    acc.push(`(${readOnly})`);
                    return acc;
                } catch (e) {
                    acc.push(Placeholder.NO_DATA);
                    acc.push(Placeholder.NO_DATA);
                    return acc;
                }
            }, []),
            `${
                typeof actualAverageTotalPoints === 'number'
                    ? Number(actualAverageTotalPoints.toFixed(3))
                    : actualAverageTotalPoints
            }`,
            ...assignment.rubric.criteria.reduce((acc, { id: criterionId, options }) => {
                try {
                    const criterionTotal = gradedSubmissions.reduce((acc, cur) => {
                        const optionId = cur.grading.criteria[criterionId];
                        return acc + Number(options.find(({ id }) => id === optionId)?.rate ?? 0);
                    }, 0);
                    acc.push(
                        gradedSubmissions.length
                            ? `${Number((criterionTotal / gradedSubmissions.length).toFixed(3))}`
                            : Placeholder.NO_DATA
                    );
                    acc.push(`(${readOnly})`);
                    return acc;
                } catch (e) {
                    acc.push(Placeholder.NO_DATA);
                    acc.push(Placeholder.NO_DATA);
                    return acc;
                }
            }, []),
            `(${readOnly})`,
            `(${readOnly})`,
            `(${readOnly})`,
            `(${readOnly})`,
        ],
        ...submissions.map((submission) => {
            let totalSuggestedPoints = Placeholder.NO_DATA;
            try {
                if (submission.ai?.grading) {
                    totalSuggestedPoints = getRubricTotalSuggestedPoints(assignment, submission);
                }
            } catch (e) {
                totalSuggestedPoints = Placeholder.NO_DATA;
            }

            let totalActualPoints = Placeholder.NO_DATA;
            try {
                if (submission.grading) {
                    totalActualPoints =
                        submission.grade_overrides?.total ??
                        assignment.rubric.criteria.reduce((acc, { id, options }) => {
                            const optionId = submission.grading.criteria[id];
                            const overrideRate = submission.grade_overrides?.[id];

                            if (overrideRate) return acc + Number(overrideRate);
                            if (optionId) {
                                const option = options.find(({ id }) => id === optionId);
                                return acc + (option ? Number(option.rate) : 0);
                            }
                            return acc;
                        }, 0);
                }
            } catch (e) {
                totalActualPoints = Placeholder.NO_DATA;
            }
            const suggestedFeedback = formatFeedbackStr(submission.ai?.feedback);
            const finalFeedback = formatFeedbackStr(submission.grading?.feedback);

            return [
                `${submission.files.map(({ name, original_file }) => (original_file ? original_file.name : name)).join(', ')}`,
                ...(assignment.api_assignment_id
                    ? [
                          parseName(submission?.api?.user?.name ?? '').first,
                          parseName(submission?.api?.user?.name ?? '').last,
                          submission?.api?.user?.sections?.map(({ name }) => name)?.join(', ') ?? '--',
                      ]
                    : []),
                `${totalSuggestedPoints}`,
                ...assignment.rubric.criteria.reduce((acc, { id, options }) => {
                    try {
                        const suggestedOptionId = submission.ai.grading[id].optionId;
                        acc.push(Number(options.find(({ id }) => id === suggestedOptionId).rate).toString());
                        acc.push(options.find(({ id }) => id === suggestedOptionId).title);
                        return acc;
                    } catch (e) {
                        acc.push(Placeholder.NO_DATA);
                        acc.push(Placeholder.NO_DATA);
                        return acc;
                    }
                }, []),
                `${totalActualPoints}`,
                ...assignment.rubric.criteria.reduce((acc, { id, options }) => {
                    try {
                        const optionId = submission.grading.criteria[id];
                        const overrideRate = submission.grade_overrides?.[id];
                        acc.push(
                            overrideRate
                                ? Number(overrideRate)
                                : Number(options.find(({ id }) => id === optionId).rate).toString()
                        );
                        acc.push(
                            options.find(({ id, rate }) => {
                                if (optionId) return id === optionId;
                                return rate === overrideRate;
                            }).title || Placeholder.NO_DATA
                        );
                        return acc;
                    } catch (e) {
                        acc.push(Placeholder.NO_DATA);
                        acc.push(Placeholder.NO_DATA);
                        return acc;
                    }
                }, []),
                getSubmissionGradingTime(submission),
                getSubmissionGradedBy(submission, accountUsers),
                suggestedFeedback,
                finalFeedback,
            ];
        }),
    ];

    return csv;
};

export const exportSubmissionsToCSV = (course, assignment, submissions, accountUsers, t) =>
    downloadCSV(
        `${course.name}-${assignment.title}.csv`.toLowerCase().replaceAll(' ', '_'),
        buildSubmissionsCSV(course, assignment, submissions, accountUsers, t)
    );

export const buildRubricCSV = (rubric, t) => {
    const criteriaText = t ? t('assignment:rubric.criteria', 'Criteria') : 'Criteria';
    const pointsText = t ? t('assignment:rubric.points-label', 'Points') : 'Points';
    const pointsShortText = (t ? t('assignment:rubric.criterion-points', 'pts') : 'pts').toLowerCase();
    const ratingsText = t ? t('assignment:rubric.ratings-label', 'Ratings') : 'Ratings';
    const totalPointsText = t ? t('assignment:rubric.total-points', 'Total Points') : 'Total Points';

    if (isUnscoredRubric(rubric)) {
        return [
            [rubric.title],
            [criteriaText, ratingsText],
            ...rubric.criteria
                .map((criterion, index) => {
                    return [
                        ...(index ? [[]] : []),
                        [
                            criterion.title,
                            ...criterion.options.map((option) => {
                                const level = getRubricOptionLevel(criterion, option);
                                const levelText = t
                                    ? t('assignment:rubric.level-value', 'Lv. {{level}}', { level })
                                    : `Lv. ${level}`;
                                return `(${levelText}) ${option.title}`;
                            }),
                        ],
                        [
                            '',
                            ...criterion.options.map((option) => {
                                return option.description;
                            }),
                        ],
                    ];
                })
                .flat(),
        ];
    }

    const rubricTotalPoints = roundTo(
        rubric.criteria.reduce((acc, cur) => {
            const criterionTotalPoints = Math.max(...cur.options.map(({ rate }) => Number(rate)));
            return acc + criterionTotalPoints;
        }, 0)
    );
    const csv = [
        [rubric.title],
        [criteriaText, pointsText, ratingsText],
        ...rubric.criteria
            .map((criterion, index) => {
                const criterionTotalPoints = Math.max(...criterion.options.map(({ rate }) => Number(rate)));
                return [
                    ...(index ? [[]] : []),
                    [
                        criterion.title,
                        `${criterionTotalPoints} ${pointsShortText}`,
                        ...criterion.options.map((option) => {
                            return `(${option.rate} ${pointsShortText}) ${option.title}`;
                        }),
                    ],
                    [
                        '',
                        '',
                        ...criterion.options.map((option) => {
                            return option.description;
                        }),
                    ],
                ];
            })
            .flat(),
        [totalPointsText, `${rubricTotalPoints} ${pointsShortText}`],
    ];
    return csv;
};

export const exportRubricToCSV = (rubric, t) =>
    downloadCSV(`${rubric.title}.csv`.toLowerCase().replaceAll(' ', '_'), buildRubricCSV(rubric, t));

export const getTemplatedFeedback = (assignment, form, field = 'ai_feedback_settings') => {
    let f = '';
    assignment.rubric.criteria.forEach(({ id, title }, index) => {
        const optionId = field === 'ai_feedback_settings' ? form.criteria[id] : form.criteria[id].optionId;
        if (!optionId) return;
        const feedback = assignment[field].templated[id]?.[optionId];
        f += `C${index + 1}: ${title}\n`;
        f += feedback;
        if (index !== assignment.rubric.criteria.length - 1) f += `\n\n`;
    });
    return f;
};

// default using js built-in in comparator for numeric and string values
export const commonDescendingComparator = (a, b, getter) => {
    const valueA = getter(a);
    const valueB = getter(b);
    if (valueB < valueA) {
        return -1;
    }
    if (valueB > valueA) {
        return 1;
    }
    return 0;
};

export const momentDescendingComparator = (a, b, getter) => {
    const dateA = getter(a);
    const dateB = getter(b);

    // Handle if dates do not exist:
    // Both null -> equal
    if (!dateA && !dateB) {
        return 0;
    }
    // A is null, B is not -> B is greater, so B should come first
    if (!dateA && dateB) {
        return 1;
    }
    // B is null, A is not -> A is greater, so A should come first
    if (dateA && !dateB) {
        return -1;
    }

    // Now both are not null, verify they are valid moment objects
    if (!moment.isMoment(dateA) || !moment.isMoment(dateB)) {
        throw new Error('Getter must return a valid moment.js object or null/undefined.');
    }

    // Compare actual dates in descending order
    if (dateB.isBefore(dateA)) {
        return -1;
    }
    if (dateB.isAfter(dateA)) {
        return 1;
    }
    return 0;
};

export const canAccessFeature = (feature, user, customer, entitlements) => {
    if (process.env.AUTH !== 'PASSWORD') return true;
    if (user.has_full_access) return true;
    return entitlements.some(({ lookup_key }) => lookup_key === feature);
};

export const cancelBatchEvaluation = async ({ currentAssignment, batch }) => {
    const payload = {
        ...batch[0],
        auth: `Bearer ${localStorage.getItem('auth_token')}`,
        platform: process.env.REACT_APP_API_ENDPOINT.split('/').slice(-1)[0],
        content_bank_resources: currentAssignment.content_bank_resources,
    };
    const response = await getChatCompletion(AI_BATCH_CANCEL_URL, JSON.stringify(payload));
    return response;
};

export const cancelPreview = async ({ currentAssignment, submissionId }) => {
    const payload = {
        submission_id: submissionId,
        auth: `Bearer ${localStorage.getItem('auth_token')}`,
        platform: process.env.REACT_APP_API_ENDPOINT.split('/').slice(-1)[0],
        content_bank_resources: currentAssignment.content_bank_resources,
        type: 'preview',
    };
    const response = await getChatCompletion(AI_BATCH_CANCEL_URL, JSON.stringify(payload));
    return response;
};

export function compareCanvasRubrics(data, criteriaArray, rubricSettings) {
    const rubric = data?.rubric ?? data;
    // Ensure data has criteria
    if (!rubric?.criteria || !Array.isArray(rubric?.criteria)) {
        return false;
    }
    if (!criteriaArray || criteriaArray?.length === 0) return false;
    if (rubric.criteria.length !== criteriaArray.length) return false;
    if (rubricSettings && isUnscoredRubric(data) !== (rubricSettings.hide_points === true)) return false;
    const unscoredRubric = isUnscoredRubric(data);
    // Loop over each criterion in the first object
    for (const [criterionIndex, criterion] of rubric.criteria.entries()) {
        // Find matching criterion in the second array by id
        const matchingCriterion = unscoredRubric
            ? criteriaArray[criterionIndex]
            : criteriaArray.find((item) => `${item.id}` === `${criterion.id}`);
        if (!matchingCriterion || `${matchingCriterion.id}` !== `${criterion.id}`) {
            console.log('criterion not matched');
            return false;
        }
        if (
            normalizeRubricText(criterion.title ?? '') !== normalizeRubricText(matchingCriterion.description ?? '') ||
            normalizeRubricText(criterion.description ?? '') !==
                normalizeRubricText(matchingCriterion.long_description ?? '')
        ) {
            console.log('criterion text not matched');
            return false;
        }

        // Check that the options and ratings match
        if (
            !criterion.options ||
            !Array.isArray(criterion.options) ||
            !matchingCriterion.ratings ||
            !Array.isArray(matchingCriterion.ratings)
        ) {
            console.log('options notmatched');
            return false;
        }
        if (criterion.options.length !== matchingCriterion.ratings.length) return false;

        for (const [optionIndex, option] of criterion.options.entries()) {
            // Find a matching rating by id in the ratings array
            const matchingRating = unscoredRubric
                ? matchingCriterion.ratings[optionIndex]
                : matchingCriterion.ratings.find((rating) => `${rating.id}` === `${option.id}`);
            if (!matchingRating || `${matchingRating.id}` !== `${option.id}`) {
                console.log('rate id notmatched');
                return false;
            }
            if (
                normalizeRubricText(option.title ?? '') !== normalizeRubricText(matchingRating.description ?? '') ||
                normalizeRubricText(option.description ?? '') !==
                    normalizeRubricText(matchingRating.long_description ?? '')
            ) {
                console.log('rating text not matched');
                return false;
            }

            // Compare rate (as a number) with points
            if (!unscoredRubric) {
                const optionRateMissing = option.rate === null || option.rate === undefined || option.rate === '';
                const ratingPointsMissing = matchingRating.points === null || matchingRating.points === undefined;
                if (
                    optionRateMissing !== ratingPointsMissing ||
                    (!optionRateMissing && Number(option.rate) !== Number(matchingRating.points))
                ) {
                    console.log('rate number notmatched');
                    return false;
                }
            }
        }
    }
    return true;
}

export function compareD2lRubrics(assignmentOrRubric, d2lRubricOrGroups) {
    const myRubric = assignmentOrRubric?.rubric ?? assignmentOrRubric;
    if (!myRubric?.criteria || !Array.isArray(myRubric.criteria)) {
        console.error("My rubric is missing a valid 'criteria' array.");
        return false;
    }

    const d2lRubric = Array.isArray(d2lRubricOrGroups) ? { CriteriaGroups: d2lRubricOrGroups } : d2lRubricOrGroups;
    const d2lRubricGroups = d2lRubric?.CriteriaGroups;
    if (!Array.isArray(d2lRubricGroups)) {
        console.error('D2L rubric must contain an array of criteria groups.');
        return false;
    }

    const unscoredRubric =
        myRubric.scoring_mode === 'unscored' ||
        (myRubric.scoring_mode !== 'scored' && isUnscoredRubric(assignmentOrRubric));
    if (
        d2lRubric.ScoringMethod !== null &&
        d2lRubric.ScoringMethod !== undefined &&
        unscoredRubric !== (Number(d2lRubric.ScoringMethod) === 0)
    ) {
        console.error('D2L rubric scoring method does not match.');
        return false;
    }

    const flatD2l = d2lRubricGroups.reduce((all, group) => {
        const levels = Array.isArray(group.Levels) ? group.Levels : [];
        const criteria = Array.isArray(group.Criteria) ? group.Criteria : [];
        for (const crit of criteria) {
            all.push({
                id: String(crit.Id),
                name: crit.Name,
                levels,
                cells: Array.isArray(crit.Cells) ? crit.Cells : [],
            });
        }
        return all;
    }, []);

    if (myRubric.criteria.length !== flatD2l.length) {
        console.error(`Mismatch: myRubric has ${myRubric.criteria.length} criteria but D2L has ${flatD2l.length}`);
        return false;
    }

    for (const [criterionIndex, myCrit] of myRubric.criteria.entries()) {
        const critId = String(myCrit.id);
        const d2lCrit = unscoredRubric ? flatD2l[criterionIndex] : flatD2l.find((c) => c.id === critId);
        if (!d2lCrit || d2lCrit.id !== critId) {
            console.error('No D2L criterion for id', critId);
            return false;
        }
        if ((myCrit.title ?? '').trim() !== (d2lCrit.name ?? '').trim()) {
            console.error('D2L criterion name mismatch for id', critId);
            return false;
        }

        if (!Array.isArray(myCrit.options) || myCrit.options.length !== d2lCrit.cells.length) {
            console.error('Option count mismatch for criterion', critId);
            return false;
        }

        const matchedCellIndexes = new Set();
        for (const [optionIndex, opt] of myCrit.options.entries()) {
            const optionId = String(opt.id);
            const optionPrefix = `${critId}-`;
            if (!optionId.startsWith(optionPrefix)) {
                console.error('Invalid option.id format', opt.id);
                return false;
            }

            const levelId = optionId.slice(optionPrefix.length);
            const levelIndex = d2lCrit.levels.findIndex(({ Id }) => `${Id}` === levelId);
            let cellIndex = d2lCrit.cells.findIndex(
                (cell, index) =>
                    !matchedCellIndexes.has(index) &&
                    cell?.LevelId !== null &&
                    cell?.LevelId !== undefined &&
                    `${cell.LevelId}` === levelId
            );
            if (cellIndex === -1 && levelIndex !== -1 && !matchedCellIndexes.has(levelIndex)) {
                cellIndex = levelIndex;
            }
            if (cellIndex === -1) {
                console.error('No D2L cell for level', levelId, 'in criterion', critId);
                return false;
            }
            if (unscoredRubric && (levelIndex !== optionIndex || cellIndex !== optionIndex)) {
                console.error('TextOnly level order mismatch for option', opt.id);
                return false;
            }

            matchedCellIndexes.add(cellIndex);
            const cell = d2lCrit.cells[cellIndex];
            const resolvedLevelId = cell?.LevelId ?? d2lCrit.levels[cellIndex]?.Id ?? levelId;
            const levelMeta =
                d2lCrit.levels.find(({ Id }) => `${Id}` === `${resolvedLevelId}`) ?? d2lCrit.levels[cellIndex];
            if (!levelMeta) {
                console.error('No D2L level metadata for level', levelId);
                return false;
            }

            if (`${levelMeta.Id}` !== levelId) {
                console.error('Level ID mismatch for option', opt.id);
                return false;
            }
            if (opt.title !== levelMeta.Name) {
                console.error(`Title mismatch for ${opt.id}:`, opt.title, 'vs', levelMeta.Name);
                return false;
            }

            if (!unscoredRubric) {
                const d2lPoints =
                    Number(d2lRubric.ScoringMethod) === 3 ? cell?.Points : (levelMeta.Points ?? cell?.Points);
                const optionRateMissing = opt.rate === null || opt.rate === undefined || opt.rate === '';
                const d2lPointsMissing = d2lPoints === null || d2lPoints === undefined || d2lPoints === '';
                if (
                    optionRateMissing !== d2lPointsMissing ||
                    (!optionRateMissing && Number(opt.rate) !== Number(d2lPoints))
                ) {
                    console.error(`Rate mismatch for ${opt.id}:`, opt.rate, 'vs', d2lPoints);
                    return false;
                }
            }

            const myDesc = (opt.description || '').trim();
            const d2lDesc = (
                unscoredRubric
                    ? cell?.Feedback?.Text || cell?.Description?.Text || ''
                    : cell?.Description?.Text || cell?.Feedback?.Text || ''
            ).trim();
            if (myDesc !== d2lDesc) {
                console.error('Description mismatch for option', opt.id);
                return false;
            }
        }
    }

    return true;
}

export const hasD2lTextOnlyRubricMismatch = (assignment, apiData = assignment?.api_data) => {
    const d2lRubric = apiData?.Assessment?.Rubrics?.[0];
    const localRubric = assignment?.rubric ?? assignment;
    const localRubricIsUnscored =
        localRubric?.scoring_mode === 'unscored' ||
        (localRubric?.scoring_mode !== 'scored' && isUnscoredRubric(assignment));
    const lmsRubricIsTextOnly = Number(d2lRubric?.ScoringMethod) === 0;

    // D2L TextOnly rubrics are LMS-owned: TimelyGrader cannot create one or
    // change the scoring mode through the supported rubric sync flow.
    if (!localRubricIsUnscored && !lmsRubricIsTextOnly) return false;
    if (!d2lRubric || !lmsRubricIsTextOnly) return true;

    return !compareD2lRubrics({ ...assignment, api_data: apiData }, d2lRubric);
};

export const interleave = (array, separator) => {
    return array.flatMap((item, index) => (index < array.length - 1 ? [item, separator] : [item]));
};

export const sortCriteria = (criteria, sort) => {
    const sortedCriteria = cloneDeep(criteria).map((criterion) => {
        criterion.options.sort((a, b) => {
            const numA = Number(a.rate);
            const numB = Number(b.rate);
            if (!numA && !numB) return 0;
            if (a.rate === '') return 1;
            if (b.rate === '') return -1;
            if (sort === 'asc') return numA - numB;
            return numB - numA;
        });
        return criterion;
    });
    return sortedCriteria;
};

// given criteria, round every option rate to at most two decimal places, removing
// trailing zeroes where possible
export const roundCriteriaOptionRates = (criteria) => {
    const roundToTwoDecimals = (str) => {
        if (typeof str !== 'string') return null;
        const num = Number(str);
        if (!Number.isFinite(num) || num < 0) return null;
        return parseFloat(num.toFixed(2)).toString();
    };

    return criteria.map((criterion) => {
        return {
            ...criterion,
            options: criterion.options.map((option) => {
                return { ...option, rate: roundToTwoDecimals(option.rate) };
            }),
        };
    });
};

// rounds a number to a given number of decimal places using standard rounding
export const roundTo = (num, decimals = 2) => {
    const factor = 10 ** decimals;
    return Math.round(num * factor) / factor;
};

export const getSubmissionGradingTimeInSeconds = (submission) => {
    if (!submission.sessions) return 0;
    return Object.values(submission.sessions)
        .flat()
        .filter(({ reason }) => reason === 'confirm')
        .reduce((acc, cur) => acc + cur.duration, 0);
};

export const getSubmissionGradingTime = (submission) => {
    if (!submission.sessions) return '--';
    const sessionsEndWithConfirm = Object.values(submission.sessions)
        .flat()
        .filter(({ reason }) => reason === 'confirm');
    if (!sessionsEndWithConfirm.length) return '--';
    const numSeconds = getSubmissionGradingTimeInSeconds(submission);
    const duration = moment.duration(numSeconds, 'seconds');
    return moment.utc(duration.asMilliseconds()).format('mm:ss');
};

export const getSubmissionGradedBy = (submission, accountUsers) => {
    if (!submission.grading || !submission.sessions) return '--';
    const graderOwnerAccountId = getLatestConfirmedId(submission.sessions);
    const targetAccountUser = accountUsers.find(({ owner_account_id }) => owner_account_id === graderOwnerAccountId);
    if (!targetAccountUser) return '--';
    return getUserPreferredFullName(targetAccountUser);
};

export const getCriterionMaxPoints = (criterion) => {
    return Math.max(...criterion.options.map(({ rate }) => Number(rate)));
};

export const getRubricMaxPoints = (criteria) => {
    return criteria.reduce((acc, criterion) => {
        return acc + getCriterionMaxPoints(criterion);
    }, 0);
};

export const getRubricTotalSuggestedPoints = (assignment, submission) => {
    return assignment.rubric.criteria.reduce((acc, { id, options }) => {
        const optionId = submission.ai.grading[id]?.optionId;
        if (!optionId) {
            return acc;
        }
        const option = options.find((o) => o.id === optionId);
        return acc + (option ? Number(option.rate) : 0);
    }, 0);
};

export const getSuggestedGradeCriteria = (submission, excludedCriterionIds = []) => {
    const excludedCriterionIdsSet = new Set(excludedCriterionIds);
    return Object.fromEntries(
        Object.entries(submission.ai?.grading ?? {})
            .filter(
                ([criterionId, suggestion]) =>
                    !excludedCriterionIdsSet.has(criterionId) && Boolean(suggestion?.optionId)
            )
            .map(([criterionId, { optionId }]) => [criterionId, optionId])
    );
};

export const lowercaseExtension = (filename) => {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) return filename; // No extension found
    const name = filename.substring(0, lastDotIndex);
    const ext = filename.substring(lastDotIndex + 1).toLowerCase();
    return `${name}.${ext}`;
};

export const pickOptionByRange = (criterion, rawValue) => {
    if (!criterion || !Array.isArray(criterion.options) || !criterion.options.length) return null;

    const v = Number(rawValue);
    if (!Number.isFinite(v)) return null;
    const value = Math.max(0, v);

    const sorted = [...criterion.options]
        .map((o) => ({ ...o, rate: Number(o.rate ?? o.points ?? 0) }))
        .sort((a, b) => a.rate - b.rate);

    // Ceiling logic: find the smallest rate >= value
    let idx = sorted.findIndex((o) => o.rate >= value);

    // Special case: raw value = 0 -> pick the lowest non-zero option
    if (value === 0) {
        const firstPositive = sorted.find((o) => o.rate > 0);
        return firstPositive ?? sorted[0];
    }

    // If no ceiling match (value above all options), pick highest
    if (idx === -1) idx = sorted.length - 1;

    return sorted[idx];
};

const getEarliestConfirmedId = (sessions) => {
    if (!sessions) return null;

    let earliestId = null;
    let earliestTimestamp = Infinity;

    for (const [id, sessionArray] of Object.entries(sessions)) {
        if (!Array.isArray(sessionArray)) continue;
        for (const session of sessionArray) {
            if (session.reason === 'confirm' && session.created_on < earliestTimestamp) {
                earliestTimestamp = session.created_on;
                earliestId = id;
            }
        }
    }

    return earliestId;
};

const getEarliestConfirmedTimestamp = (sessions) => {
    if (!sessions) return null;

    let earliestTimestamp = Infinity;

    for (const sessionArray of Object.values(sessions)) {
        if (!Array.isArray(sessionArray)) continue;
        for (const session of sessionArray) {
            if (session.reason === 'confirm' && session.created_on < earliestTimestamp) {
                earliestTimestamp = session.created_on;
            }
        }
    }

    return earliestTimestamp;
};

export const getLatestConfirmedId = (sessions) => {
    // we decided to temporarily change this to display the first grader
    // see https://app.asana.com/1/1136003531356563/project/1211333976013617/task/1211582472352674?focus=true
    return getEarliestConfirmedId(sessions);

    // if (!sessions) return null;

    // let latestId = null;
    // let latestTimestamp = -Infinity;

    // for (const [id, sessionArray] of Object.entries(sessions)) {
    //     if (!Array.isArray(sessionArray)) continue;
    //     for (const session of sessionArray) {
    //         if (session.reason === 'confirm' && session.created_on > latestTimestamp) {
    //             latestTimestamp = session.created_on;
    //             latestId = id;
    //         }
    //     }
    // }

    // return latestId;
};

export const getLatestConfirmedTimestamp = (sessions) => {
    return getEarliestConfirmedTimestamp(sessions);
};

export const getPrettyRole = (role, t) => {
    return {
        instructor: t('common:ui.instructor', 'Instructor'),
        student: t('common:ui.student', 'Student'),
        ta: t('common:ui.ta', 'TA'),
    }[role];
};

export const getPrettySections = (sections) => {
    if (!sections) return '--';
    return sections.map(({ name }) => name).join(', ');
};

export const resolveFeatureFlags = (assignment, features) => {
    return {
        ...assignment,
        features: {
            grading: features?.assignment_grading ?? true,
            suggestion_tags: features?.assignment_suggestion_tag ?? true,
            feedback: features?.assignment_feedback ?? true,
            accept_all: features?.assignment_accept_all ?? false,
            accept_single: features?.assignment_accept_single_submission ?? true,
            inline_comments: features?.assignment_inline_comments ?? true,
            rubric: features?.assignment_rubric ?? true,
        },
        ai_grading_enabled: features?.assignment_grading !== false && assignment.ai_grading_enabled,
        ai_feedback_enabled: features?.assignment_feedback !== false && assignment.ai_feedback_enabled,
        ai_inline_comments_enabled:
            features?.assignment_inline_comments !== false && assignment.ai_inline_comments_enabled,
    };
};

export const isAllowedMimeType = (fileType, allowedTypes) => {
    return allowedTypes.some((allowed) => {
        if (!allowed.endsWith('/*')) {
            return fileType === allowed;
        }

        const baseType = allowed.slice(0, allowed.length - 1);
        return fileType.startsWith(baseType);
    });
};

export const downloadStringAsTxt = (filename, text) => {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    URL.revokeObjectURL(url);
};

export const normalize = (s) =>
    s
        .normalize('NFKC')
        .replace(/[‘’‛]/g, "'")
        .replace(/[“”„‟]/g, '"')
        .toLowerCase();

export const last4 = (str) => {
    if (typeof str !== 'string') return '';
    return str.slice(-4);
};

export const formatDuration = (sec, format = DurationFormat.MIN_SEC) => {
    const hour = Math.floor(sec / 3600);
    const min = Math.floor((sec % 3600) / 60);
    const secRemainder = Math.floor(sec % 60);
    const totalMins = Math.floor(sec / 60);

    if (format === DurationFormat.HOUR_MIN) {
        return `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
    }
    return `${totalMins}:${secRemainder.toString().padStart(2, '0')}`;
};

export const formatPercent = (
    numerator,
    denominator = 1,
    { decimals = 0, zeroLabel: zeroOverride = '0%', keepTrailingZeros = false } = {}
) => {
    const num = parseFloat(numerator);
    const den = parseFloat(denominator);

    if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0 || num <= 0) {
        return zeroOverride;
    }

    const percent = (num / den) * 100;

    if (Number.isInteger(percent) && !keepTrailingZeros) {
        return `${percent}%`;
    }

    return `${percent.toFixed(decimals)}%`;
};

export const formatMinutesToReadableTime = (time, hrUnit = 'hr', minUnit = 'min') => {
    const isInvalid = typeof time !== 'number' || Number.isNaN(time) || time === Placeholder.NO_DATA;
    if (isInvalid) return `${Placeholder.NO_DATA} ${minUnit}`;

    if (time < 0) return 'N/A';
    if (time === 0) return '0 ' + minUnit;

    const hours = Math.floor(time / 60);
    const minutes = Math.round(time % 60);

    const result = [];

    if (hours > 0) {
        result.push(`${hours} ${hrUnit}`);
    }

    if (minutes > 0 || hours === 0) {
        result.push(`${minutes} ${minUnit}`);
    }

    return result.join(' ');
};

export const buildGraderStatisticsCSV = (assignment, calculatedGraders, totalSubmissions, t) => {
    if (!calculatedGraders || !assignment) return null;

    const unscoredRubric = isUnscoredRubric(assignment);
    const totalSubmissionsLabel = t ? t('common:table.total-submissions', 'Total Submissions') : 'Total Submissions';
    const graderNameLabel = t ? t('common:table.grader-name', 'Grader Name') : 'Grader Name';
    const submissionsGradedLabel = t
        ? t('common:table.submissions-graded', 'Submissions Graded')
        : 'Submissions Graded';
    const countLabel = t ? t('ui.count', 'count') : 'count';
    const submissionsGradedCountLabel = `${submissionsGradedLabel} (${countLabel})`;
    const submissionsGradedPercentageLabel = `${submissionsGradedLabel} (%)`;
    const avgGradeGivenLabel =
        (unscoredRubric
            ? t
                ? t('common:table.avg-level-attainment', 'Avg Level Attainment')
                : 'Avg Level Attainment'
            : t
              ? t('common:table.avg-grade-given', 'Avg Grade Given')
              : 'Avg Grade Given') + ' (%)';
    const aiAccuracyLabel = (t ? t('common:table.ai-accuracy', 'AI Accuracy') : 'AI Accuracy') + ' (%)';
    const totalGradingTimeLabel = t
        ? t('common:table.total-grading-time-hhmm', 'Total Grading Time (hh:mm)')
        : 'Total Grading Time (hh:mm)';
    const avgGradingTimeLabel = t
        ? t('common:table.avg-grading-time-mmss', 'Avg Grading Time (mm:ss)')
        : 'Avg Grading Time (mm:ss)';

    const rows = calculatedGraders
        .filter((grader) => grader.submissionsGraded > 0 || grader.rawTime > 0)
        .map((grader, index) => {
            return [
                index === 0 ? totalSubmissions : '',
                grader.name,
                grader.submissionsGraded,
                grader.submissionsGradedPercentage,
                grader.avgGrade,
                grader.aiAccuracy,
                grader.totalTime,
                grader.avgTime,
            ];
        });

    const csvHeaders = [
        totalSubmissionsLabel,
        graderNameLabel,
        submissionsGradedCountLabel,
        submissionsGradedPercentageLabel,
        avgGradeGivenLabel,
        aiAccuracyLabel,
        totalGradingTimeLabel,
        avgGradingTimeLabel,
    ];

    return [csvHeaders, ...rows];
};

export const exportGraderStatisticsToCSV = (assignment, calculatedGraders, totalSubmissions, t) => {
    const data = buildGraderStatisticsCSV(assignment, calculatedGraders, totalSubmissions, t);
    if (!data) return;

    return downloadCSV(`Grader_Performance_Statistics_${assignment.title.replace(/\s+/g, '_')}.csv`, data);
};

export const buildGradingStatisticsCSV = (assignment, detailedGradingData, t) => {
    const isApiAssignment = !!assignment.api_assignment_id;
    const unscoredRubric = isUnscoredRubric(assignment);
    const rows = [];
    const formatLevel = (value) => (value === null || value === undefined ? '--' : `Lv. ${value}`);

    const studentIdentifier = t ? t('common:table.student-identifier', 'Student Identifier') : 'Student Identifier';
    const fileName = t ? t('common:table.file-name', 'File Name') : 'File Name';
    const sections = t ? t('common:table.section-s', 'Section(s)') : 'Section(s)';
    const gradedBy = t ? t('common:table.graded-by', 'Graded By') : 'Graded By';
    const criterion = t ? t('common:table.criterion', 'Criterion') : 'Criterion';
    const instructorSelectedGrade = unscoredRubric
        ? t
            ? t('common:table.manual-level-attainment', 'Manual Level Attainment')
            : 'Manual Level Attainment'
        : t
          ? t('common:table.instructor-selected-grade', 'Instructor Selected Grade')
          : 'Instructor Selected Grade';
    const instructorSelectedRating = t
        ? t('common:table.instructor-selected-rating', 'Instructor Selected Rating')
        : 'Instructor Selected Rating';
    const aiSuggestedGrade = unscoredRubric
        ? t
            ? t('common:table.ai-level-attainment', 'AI Level Attainment')
            : 'AI Level Attainment'
        : t
          ? t('common:table.ai-suggested-grade', 'AI Suggested Grade')
          : 'AI Suggested Grade';
    const aiSuggestedRating = t ? t('common:table.ai-suggested-rating', 'AI Suggested Rating') : 'AI Suggested Rating';
    const aiAccuracy = t ? t('common:table.ai-accuracy', 'AI Accuracy') : 'AI Accuracy';

    detailedGradingData.forEach((data) => {
        data.criteriaDetails.forEach((crit, index) => {
            const isFirst = index === 0;

            rows.push({
                [studentIdentifier]: isFirst ? data.studentName : '',
                [fileName]: isFirst ? data.fileName : '',
                [sections]: isFirst ? data.sections : '',
                [gradedBy]: isFirst ? data.graderName : '',
                [criterion]: crit.title,
                [instructorSelectedGrade]: unscoredRubric ? formatLevel(crit.humanRate) : crit.humanRate,
                [instructorSelectedRating]: crit.humanRating,
                [aiSuggestedGrade]: crit.aiAttempted ? (unscoredRubric ? formatLevel(crit.aiRate) : crit.aiRate) : '--',
                [aiSuggestedRating]: crit.aiRating,
                [aiAccuracy]: crit.aiAttempted ? `${(crit.accuracy * 100).toFixed(1)}%` : '--',
            });
        });

        rows.push({});
    });

    let csvHeaders = [
        studentIdentifier,
        fileName,
        sections,
        gradedBy,
        criterion,
        instructorSelectedGrade,
        instructorSelectedRating,
        aiSuggestedGrade,
        aiSuggestedRating,
        aiAccuracy,
    ];

    if (!isApiAssignment) {
        csvHeaders = csvHeaders.filter((header) => ![studentIdentifier, sections].includes(header));
    }

    const data = [csvHeaders, ...rows.map((row) => csvHeaders.map((header) => row[header] ?? ''))];

    return data;
};

export const exportGradingStatisticsToCSV = (assignment, detailedGradingData, t) => {
    const data = buildGradingStatisticsCSV(assignment, detailedGradingData, t);
    return downloadCSV(`Grading_Statistics_${assignment.title.replace(/\s+/g, '_')}.csv`, data);
};

/**
 * Scrolls a DOM element into view by its ID.
 *
 * @param {string} elementId - The ID of the element to scroll to.
 * @param {ScrollIntoViewOptions} [options={ behavior: 'smooth', block: 'start' }] - Options defining the scroll behavior and alignment.
 */
export const scrollElementIntoView = (elementId, options = { behavior: 'smooth', block: 'start' }) => {
    const element = document.getElementById(elementId);
    if (element) {
        element.scrollIntoView(options);
    }
};

/**
 * Converts a zero-based numeric index into an Excel-style alphabetical column string.
 *
 * @param {number} index - The zero-based column index (e.g., 0, 25, 26).
 * @returns {string} The corresponding column letter(s) (e.g., 'A', 'Z', 'AA').
 *
 * @example
 * indexToLetters(0);  // Returns 'A'
 * indexToLetters(25); // Returns 'Z'
 * indexToLetters(26); // Returns 'AA'
 */
export const indexToLetters = (index) => {
    let letter = '';
    let currentIndex = index;

    while (currentIndex >= 0) {
        letter = String.fromCharCode((currentIndex % 26) + 65) + letter;
        currentIndex = Math.floor(currentIndex / 26) - 1;
    }
    return letter;
};

export const isFeedbackSettingsDefault = (form) => {
    if (form.structure === 'rubric') {
        return (
            form.suggestionLength === DEFAULT_FEEDBACK_SUGGESTION_LENGTH &&
            form.languageLevel === DEFAULT_FEEDBACK_LANGUAGE_LEVEL &&
            form.tone === DEFAULT_FEEDBACK_TONE
        );
    }
    if (form.structure === 'system') {
        return (
            form.numSuggestions === DEFAULT_FEEDBACK_NUM_SUGGESTIONS &&
            form.suggestionLength === DEFAULT_FEEDBACK_SUGGESTION_LENGTH &&
            form.languageLevel === DEFAULT_FEEDBACK_LANGUAGE_LEVEL &&
            form.tone === DEFAULT_FEEDBACK_TONE
        );
    }
    return false;
};

export const isInlineCommentsSettingsDefault = (form) => {
    return (
        form.maxCommentsPerPage === DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE &&
        form.commentLength === DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH &&
        form.languageLevel === DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL &&
        form.tone === DEFAULT_INLINE_COMMENTS_TONE
    );
};

export const getLanguageLevelDescriptionText = (languageLevel, t) => {
    return {
        'middle-school': t(
            'feedback-settings.language-level.middle-school-description',
            'Middle School: Plain and straightforward feedback. Ideal for middle school students or those with basic knowledge.'
        ),
        'high-school': t(
            'feedback-settings.language-level.high-school-description',
            'High School: Moderately complex feedback. Suited for high school students or those with intermediate understanding.'
        ),
        undergraduate: t(
            'feedback-settings.language-level.undergraduate-description',
            'Undergraduate (Default): Complex feedback requiring higher-level understanding. Geared towards college-level learners or those with advanced knowledge.'
        ),
        graduate: t(
            'feedback-settings.language-level.graduate-description',
            'Graduate +: Very intricate feedback demanding advanced comprehension. Targeted at graduate students or individuals with expert-level understanding.'
        ),
    }[languageLevel];
};

export const getToneDescriptionText = (tone, t) => {
    return {
        'very-supportive': t(
            'feedback-settings.tone.very-supportive-description',
            'Very Supportive: Feedback is overwhelmingly positive and focuses primarily on strengths, offering minimal critique or suggestions for improvement.'
        ),
        supportive: t(
            'feedback-settings.tone.supportive-description',
            'Supportive: Feedback is generally positive, highlighting strengths while gently pointing out areas for improvement in a supportive manner.'
        ),
        balanced: t(
            'feedback-settings.tone.balanced-description',
            'Balanced (Default): Feedback strikes a balance between positive reinforcement and constructive criticism, providing both praise and suggestions for improvement in equal measure.'
        ),
        critical: t(
            'feedback-settings.tone.critical-description',
            'Critical:  Feedback is straightforward and analytical, pinpointing mistakes and areas needing improvement directly. It emphasizes the importance of addressing these areas to facilitate growth and development.'
        ),
    }[tone];
};

export const getSuggestionLengthText = (suggestionLength, t) => {
    return {
        '<25': t('common:word-length.under-word-count', 'Under {{wordCount}} words', { wordCount: 25 }),
        '25-50': t('common:word-length.word-count-range', '{{wordCountRange}} words', { wordCountRange: '25-50' }),
        '50-75': t('common:word-length.word-count-range', '{{wordCountRange}} words', { wordCountRange: '50-75' }),
        '<50': t('common:word-length.under-word-count', 'Under {{wordCount}} words', { wordCount: 50 }),
        '50-150': t('common:word-length.word-count-range', '{{wordCountRange}} words', { wordCountRange: '50-150' }),
        '150-300': t('common:word-length.word-count-range', '{{wordCountRange}} words', { wordCountRange: '150-300' }),
        '300-450': t('common:word-length.word-count-range', '{{wordCountRange}} words', { wordCountRange: '300-450' }),
        '450-600': t('common:word-length.word-count-range', '{{wordCountRange}} words', { wordCountRange: '450-600' }),
    }[suggestionLength];
};

export const getLanguageLevelText = (languageLevel, t) => {
    return {
        'middle-school': t('feedback-settings.language-level.middle-school-label', 'Middle School'),
        'high-school': t('feedback-settings.language-level.high-school-label', 'High School'),
        undergraduate: t('feedback-settings.language-level.undergraduate-label', 'Undergraduate'),
        graduate: t('feedback-settings.language-level.graduate-label', 'Graduate'),
    }[languageLevel];
};

export const getToneText = (tone, t) => {
    return {
        'very-supportive': t('feedback-settings.tone.very-supportive-label', 'Very Supportive'),
        supportive: t('feedback-settings.tone.supportive-label', 'Supportive'),
        balanced: t('feedback-settings.tone.balanced-label', 'Balanced'),
        critical: t('feedback-settings.tone.critical-label', 'Critical'),
    }[tone];
};
